import React, { useState } from 'react';
import { FileUpload } from '../FileUpload';
import { DocumentList } from '../DocumentList';
import { Upload, FileText, Image as ImageIcon, Video } from 'lucide-react';

export function Evidence() {
  const [uploadedFiles, setUploadedFiles] = useState<Array<{
    id: string;
    name: string;
    type: string;
    content: string;
    uploadedAt: string;
  }>>([]);

  const handleFileUpload = (content: string, fileName: string) => {
    const newFile = {
      id: Date.now().toString(),
      name: fileName,
      type: getFileType(fileName),
      content,
      uploadedAt: new Date().toISOString()
    };

    setUploadedFiles(prev => [...prev, newFile]);
    
    // Save to localStorage
    const savedFiles = localStorage.getItem('evidence_files') || '[]';
    const files = JSON.parse(savedFiles);
    files.push(newFile);
    localStorage.setItem('evidence_files', JSON.stringify(files));
  };

  const getFileType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'image';
      case 'mp4':
      case 'avi':
      case 'mov':
        return 'video';
      case 'pdf':
        return 'pdf';
      default:
        return 'document';
    }
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <ImageIcon className="h-5 w-5" />;
      case 'video':
        return <Video className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  React.useEffect(() => {
    // Load files from localStorage on component mount
    const savedFiles = localStorage.getItem('evidence_files');
    if (savedFiles) {
      try {
        setUploadedFiles(JSON.parse(savedFiles));
      } catch (e) {
        console.error('Error loading evidence files:', e);
      }
    }
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Evidencias</h1>
        <p className="mt-2 text-gray-600">
          Sube y gestiona evidencias de aprendizaje y documentos de respaldo
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-xl p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Subir Evidencias
          </h2>
          <p className="text-gray-600">
            Sube imágenes, videos, documentos y otros archivos como evidencia
          </p>
        </div>
        
        <FileUpload onUploadComplete={handleFileUpload} />

        {uploadedFiles.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Archivos Subidos ({uploadedFiles.length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="text-gray-400">
                      {getFileIcon(file.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(file.uploadedAt).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                    Tipo: {file.type}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <DocumentList type="evidence" />
    </div>
  );
}